import type { BoardCard, BoardProjection } from '@nightshift/ui-types';

/**
 * The card for `ticketKey` anywhere on the board: a column, the Waiting rail or Cancelled.
 */
export function boardCardFor(board: BoardProjection | null, ticketKey: string): BoardCard | undefined {
  if (!board) return undefined;
  for (const column of board.columns) {
    const card = column.cards.find((row) => row.ticketKey === ticketKey);
    if (card) return card;
  }
  for (const group of board.waiting.groups) {
    const card = group.cards.find((row) => row.ticketKey === ticketKey);
    if (card) return card;
  }
  return board.cancelled.cards.find((row) => row.ticketKey === ticketKey);
}

/**
 * When the dependency preview should refetch the ticket it shows.
 *
 * A key that changes only when the previewed ticket itself changed: its contract revision
 * (problem and acceptance criteria), its state or its title. Every board update used to
 * refetch, and the refetch blanked the preview -- spinner, scroll to the top, focus dropped to
 * the body, where Escape then closed the whole inspector -- for a comment on another ticket.
 * When the ticket is not on the board (another project's ticket, or a filtered view), the
 * board-wide key is the only signal there is, and the refresh happens in place anyway.
 */
export function dependencyPreviewPoke(
  board: BoardProjection | null,
  ticketKey: string,
  fallback: string,
): string {
  const card = boardCardFor(board, ticketKey);
  if (!card) return fallback;
  return `${ticketKey}:${card.version ?? 'unversioned'}:${card.state}:${card.title}`;
}
